import {FeedbackAction, ISpeedOfServiceInsight} from "./coachInsightsModels";

/**
 * Formats a number of seconds as a m:ss duration string.
 */
export function formatDuration(totalSeconds: number): string {
    const rounded = Math.round(totalSeconds);
    const m = Math.floor(rounded / 60);
    const s = rounded % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Formats an hour-of-day (0-23) as a short clock label like "2PM" or "12AM".
 */
export function formatHour(hour: number): string {
    const normalized = hour % 24;
    const period = normalized >= 12 ? "PM" : "AM";
    const hour12 = normalized % 12 || 12;
    return `${hour12}${period}`;
}

/**
 * Formats a one-hour window as a range string like "2PM-3PM".
 */
export function formatHourRange(hour: number): string {
    return `${formatHour(hour)}-${formatHour(hour + 1)}`;
}

/**
 * Parses a "m:ss" SOS string (e.g. "2:58") into total seconds.
 */
export function parseSosSeconds(sos: string): number {
    const parts = sos.split(":");
    if (parts.length !== 2) return 0;
    const m = parseInt(parts[0], 10);
    const s = parseInt(parts[1], 10);
    return isNaN(m) || isNaN(s) ? 0 : m * 60 + s;
}

/**
 * Assumed average ticket value used for prototype ROI estimation.
 * Replace with real concept-level data when available.
 */
export const ASSUMED_AVG_TICKET_DOLLARS = 11.5;

/**
 * Estimates the recoverable revenue from resolving an insight.
 * Formula: impactedExperiences × avg ticket × (1 - target/avg) clipped at 0.
 */
export function estimateRoi(insight: ISpeedOfServiceInsight): number {
    const avgSec = parseSosSeconds(insight.averageSOS);
    const targetSec = parseSosSeconds(insight.targetSOS);
    if (avgSec <= 0 || targetSec <= 0 || avgSec <= targetSec) return 0;
    const overRatio = (avgSec - targetSec) / avgSec;
    return Math.round(insight.impactedExperiences * ASSUMED_AVG_TICKET_DOLLARS * overRatio);
}

export type InsightSeverity = "critical" | "high" | "medium";

/**
 * Returns a severity level based on how far over target the SOS is.
 */
export function getInsightSeverity(insight: ISpeedOfServiceInsight): InsightSeverity {
    const avgSec = parseSosSeconds(insight.averageSOS);
    const targetSec = parseSosSeconds(insight.targetSOS);
    if (targetSec <= 0) return "medium";
    const overRatio = (avgSec - targetSec) / targetSec;
    if (overRatio >= 0.2) return "critical";
    if (overRatio >= 0.1) return "high";
    return "medium";
}

/**
 * Builds a deterministic key for an insight that can be stored and matched later.
 */
export function getInsightKey(insight: ISpeedOfServiceInsight): string {
    return `${insight.locationPk}|${insight.businessDate}|${insight.dayPart}|${insight.category}`;
}

/**
 * Derives a human-readable recommended action and the closest FeedbackAction
 * based on the insight data. Intended as a prototype stand-in until the LLM
 * returns this field directly.
 */
export function deriveRecommendedAction(insight: ISpeedOfServiceInsight): {
    text: string;
    feedbackAction: FeedbackAction;
} {
    const avgSec = parseSosSeconds(insight.averageSOS);
    const targetSec = parseSosSeconds(insight.targetSOS);
    const overSec = avgSec - targetSec;
    const overRatio = targetSec > 0 ? overSec / targetSec : 0;

    if (overRatio >= 0.2) {
        return {
            text: "Contact RGM immediately — window time critical",
            feedbackAction: "ContactRGMNow"
        };
    }
    if (overRatio >= 0.1) {
        return {
            text: "Email RGM: review drive-thru staffing for this window",
            feedbackAction: "EmailRGM"
        };
    }
    if (insight.impactedExperiences >= 10) {
        return {
            text: "Add to follow-up list for next coaching session",
            feedbackAction: "AddToFollowUp"
        };
    }
    return {
        text: "Monitor — add to follow-up if pattern repeats",
        feedbackAction: "AddToFollowUp"
    };
}
